import { useState } from "react"
import { useNavigate } from "react-router-dom"
import PropTypes from "prop-types"
import { downloadXlsx } from "helpers/csv"
import { Button, DialogActions, DialogContent, List, ListItemButton, ListItemText } from "@mui/material"

import "./styles.scss"

const TRACK_LOCATION = "intraday-simulator-reload-confirmation"

export default function IntradayRebalanceReload({ destination, lastRefresh, children, type, downloadData, numberOfTrades, hasData, reloadIntraday, resetRebalance, openDialog, closeDialog, addNotification, rebalanceIsIntraday }) {
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  const trackOption = (option) => {
    window.analytics.track(TRACK_LOCATION, { option })
  }

  const reset = () => {
    reloadIntraday()
    resetRebalance()
  }

  const download = async () => {
    setLoading(true)
    const { fileName, data } = downloadData
    const success = await downloadXlsx(fileName, data, { type, location: TRACK_LOCATION })
    if (!success) {
      addNotification("error", null, "There was a problem while generating the file. Please try again")
      return false
    }
    setLoading(false)
    return true
  }

  const resetAndNavigate = () => {
    reset()
    closeDialog()
    if (destination) {
      navigate(destination)
    }
  }

  const closeAndNavigate = () => {
    closeDialog()
    if (destination) {
      navigate(destination)
    }
  }

  const downloadResetAndNavigate = async () => {
    const success = await download()
    if (success) {
      resetAndNavigate()
    }
  }

  const showDialog = () => {
    openDialog({
      title: "Confirm intraday simulator reset",
      content: <>
        <DialogContent className="intraday-rebalance-reload">
          Trades from a previous intraday update are currently loaded into the intraday simulator. Would you like to update the intraday simulator with the new intraday positions - as of&nbsp;
          <b>{lastRefresh}</b>
          ?
          <div><b>This will reset the intraday simulator screen and you will lose previous trade simulations.</b></div>
          <br />
          <div>Please select from one of the following:</div>
          <List className="option-list">
            <ListItemButton onClick={() => { trackOption("Download trades, then reset & update positions"); downloadResetAndNavigate() }} disabled={loading}>
              <ListItemText primary="Download trades, then reset & update positions" />
            </ListItemButton>
            <ListItemButton onClick={() => { trackOption("Reset trades, then update positions"); resetAndNavigate() }}>
              <ListItemText primary="Reset trades, then update positions" />
            </ListItemButton>
            {destination && <ListItemButton onClick={() => { trackOption("View previous trades"); closeAndNavigate() }}>
              <ListItemText primary="View previous trades" />
            </ListItemButton>}
          </List>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" data-test-class="cancel-intraday-reset" onClick={closeDialog}>Cancel & Return to Screen</Button>
        </DialogActions>
      </>,
    })
  }

  const onClick = () => {
    if (numberOfTrades > 0 && rebalanceIsIntraday) {
      showDialog()
    } else {
      resetAndNavigate()
    }
  }

  return hasData ? <span onClick={onClick}>{children}</span> : null
}

IntradayRebalanceReload.propTypes = {
  destination: PropTypes.string,
  lastRefresh: PropTypes.string,
  downloadData: PropTypes.object,
  numberOfTrades: PropTypes.number,
  rebalanceIsIntraday: PropTypes.bool,
  openDialog: PropTypes.func.isRequired,
  closeDialog: PropTypes.func.isRequired,
  addNotification: PropTypes.func.isRequired,
  hasData: PropTypes.bool.isRequired,
  reloadIntraday: PropTypes.func.isRequired,
  resetRebalance: PropTypes.func.isRequired,
  type: PropTypes.string,
}
